import { useMemo } from 'react';

interface Rect {
  width: number;
  height: number;
}

interface ChromeFontSizeOptions {
  /** Reference rect width — `1em` resolves to `referenceFontPx` at this width. */
  referenceWidth: number;
  /** Reference rect height — paired with `referenceWidth` for the scale calc. */
  referenceHeight: number;
  /**
   * Base font-size at scale `1`. Overlays size all chrome in `em`, so this
   * value is the implicit "1em" at the reference rect. Defaults to `10` so
   * `1em` reads as `10px` (matching every overlay's existing math).
   */
  referenceFontPx?: number;
  /** Lower bound on the scale factor. Defaults to `0.05` (effectively no floor). */
  scaleMin?: number;
  /** Upper bound on the scale factor. Defaults to `3`. */
  scaleMax?: number;
}

/**
 * Shared chrome-scaling hook for safe-zone overlays. Every overlay sizes its
 * chrome in `em` against a base `font-size` derived from the rect — this hook
 * computes that base in pixels.
 *
 * Scale factor: `min(rect.width / referenceWidth, rect.height / referenceHeight)`,
 * clamped to `[scaleMin, scaleMax]`. Returned value is `referenceFontPx * scale`.
 *
 * The `scale || 1` fallback handles a zero-area rect (avoids `NaN` / `0`
 * font-size) — when the rect hasn't been measured yet, we render at scale 1
 * rather than collapsing the chrome.
 */
export function useChromeFontSize(
  rect: Rect,
  options: ChromeFontSizeOptions,
): number {
  const {
    referenceWidth,
    referenceHeight,
    referenceFontPx = 10,
    scaleMin = 0.05,
    scaleMax = 3,
  } = options;
  return useMemo(() => {
    const scale = Math.min(
      rect.width / referenceWidth,
      rect.height / referenceHeight,
    );
    const clamped = Math.min(scaleMax, Math.max(scaleMin, scale || 1));
    return referenceFontPx * clamped;
  }, [
    rect.width,
    rect.height,
    referenceWidth,
    referenceHeight,
    referenceFontPx,
    scaleMin,
    scaleMax,
  ]);
}
